/**
 * FlahaSOIL — SoilTestLevel evidence standard (authoritative).
 *
 * `SoilTestLevel` stratifies expected input completeness so:
 *   - the API can validate uploads / score coverage,
 *   - the UI can show the correct wizard panels,
 *   - reports can declare their evidence level honestly.
 *
 * This module is the **human-readable contract**. Machine scoring lives in
 * `scientific-coverage.ts` (`SOIL_TEST_LEVEL_EXPECTATIONS`) and must stay
 * aligned with the tables below.
 *
 * Hierarchy (each level includes all lower levels):
 *
 * | Level         | Required / expected fields |
 * | ------------- | -------------------------- |
 * | PRELIMINARY   | sand, silt, clay, organicMatter, pH, EC, TDS |
 * | MODERATE      | PRELIMINARY + Ca, Mg, K, Na, Cl, N, P, lab-CEC (if provided) |
 * | ADVANCED      | MODERATE + Fe, Mn, Zn, Cu, B, Mo, S, heavy metals, carbonate, bicarbonate, SAR, ESP (+ forward-compatible extras) |
 *
 * Rules:
 * - Declared level never silently downgrades.
 * - Extra fields beyond the declared level are allowed (supplementary).
 * - EC and TDS are alternate salinity evidence (either may satisfy the slot).
 * - Lab CEC is expected at MODERATE+ when the lab reports it; derived CEC is
 *   still valid chemistry but coverage prefers a measured value when present.
 */

import { SoilTestLevel } from "./soil-domain";

/** Stable field keys used in coverage, wizard, and docs. */
export type SoilTestFieldKey =
	| "sandPercent"
	| "siltPercent"
	| "clayPercent"
	| "organicMatterPercent"
	| "pH"
	| "ecDsM"
	| "tdsMgL"
	| "ca"
	| "mg"
	| "k"
	| "na"
	| "cl"
	| "n"
	| "p"
	| "cec"
	| "fe"
	| "mn"
	| "zn"
	| "cu"
	| "b"
	| "mo"
	| "s"
	| "carbonate"
	| "bicarbonate"
	| "sar"
	| "esp"
	| "heavyMetalsJson";

export interface SoilTestFieldSpec {
	/** Wire / DTO path key. */
	key: SoilTestFieldKey;
	/** Human label for UI and reports. */
	label: string;
	/** Short unit hint (display only). */
	unit?: string;
	/**
	 * When true, any one field in the same `alternateGroup` satisfies the
	 * evidence slot (e.g. EC or TDS).
	 */
	alternateGroup?: string;
	/**
	 * Optional note for honesty (e.g. CEC may be lab-measured or derived).
	 */
	note?: string;
}

export interface SoilTestLevelStandard {
	level: SoilTestLevel;
	label: string;
	/** One-line product description. */
	summary: string;
	/** Fields newly expected at this level (not including lower levels). */
	addedFields: SoilTestFieldSpec[];
	/** Best-fit use cases (UI). */
	bestFor: string;
}

/** Fields introduced at PRELIMINARY. */
const PRELIMINARY_FIELDS: SoilTestFieldSpec[] = [
	{ key: "sandPercent", label: "Sand", unit: "%" },
	{ key: "siltPercent", label: "Silt", unit: "%" },
	{ key: "clayPercent", label: "Clay", unit: "%" },
	{ key: "organicMatterPercent", label: "Organic matter", unit: "%" },
	{ key: "pH", label: "pH" },
	{
		key: "ecDsM",
		label: "EC",
		unit: "dS/m",
		alternateGroup: "salinity",
		note: "Either EC or TDS satisfies the salinity evidence slot.",
	},
	{
		key: "tdsMgL",
		label: "TDS",
		unit: "mg/L",
		alternateGroup: "salinity",
		note: "Either EC or TDS satisfies the salinity evidence slot.",
	},
];

/** Fields added at MODERATE (on top of PRELIMINARY). */
const MODERATE_ADDED: SoilTestFieldSpec[] = [
	{ key: "ca", label: "Calcium (Ca)", unit: "cmol(+)/kg" },
	{ key: "mg", label: "Magnesium (Mg)", unit: "cmol(+)/kg" },
	{ key: "k", label: "Potassium (K)", unit: "cmol(+)/kg" },
	{ key: "na", label: "Sodium (Na)", unit: "cmol(+)/kg" },
	{ key: "cl", label: "Chloride (Cl)", unit: "mg/kg" },
	{ key: "n", label: "Nitrogen (N)", unit: "mg/kg" },
	{ key: "p", label: "Phosphorus (P)", unit: "mg/kg" },
	{
		key: "cec",
		label: "CEC (lab)",
		unit: "cmol(+)/kg",
		note: "Lab CEC when provided; engine may derive CEC if blank.",
	},
];

/** Fields added at ADVANCED (on top of MODERATE). */
const ADVANCED_ADDED: SoilTestFieldSpec[] = [
	{ key: "fe", label: "Iron (Fe)", unit: "mg/kg" },
	{ key: "mn", label: "Manganese (Mn)", unit: "mg/kg" },
	{ key: "zn", label: "Zinc (Zn)", unit: "mg/kg" },
	{ key: "cu", label: "Copper (Cu)", unit: "mg/kg" },
	{ key: "b", label: "Boron (B)", unit: "mg/kg" },
	{ key: "mo", label: "Molybdenum (Mo)", unit: "mg/kg" },
	{ key: "s", label: "Sulphur (S)", unit: "mg/kg" },
	{
		key: "heavyMetalsJson",
		label: "Heavy metals panel",
		note: "Forward-compatible JSON map of extra analytes.",
	},
	{ key: "carbonate", label: "Carbonate", unit: "mg/kg" },
	{ key: "bicarbonate", label: "Bicarbonate", unit: "mg/kg" },
	{ key: "sar", label: "SAR" },
	{ key: "esp", label: "ESP", unit: "%" },
];

/**
 * Authoritative level definitions for product, docs, UI, and reports.
 * Order is PRELIMINARY → MODERATE → ADVANCED.
 */
export const SOIL_TEST_LEVEL_STANDARD: readonly SoilTestLevelStandard[] = [
	{
		level: SoilTestLevel.PRELIMINARY,
		label: "Preliminary",
		summary:
			"Texture, organic matter, pH and salinity — reconnaissance evidence.",
		addedFields: PRELIMINARY_FIELDS,
		bestFor:
			"Quick reconnaissance, basic irrigation planning, or before a full lab panel.",
	},
	{
		level: SoilTestLevel.MODERATE,
		label: "Moderate",
		summary:
			"Adds cation panel, macros (N/P/Cl) and lab CEC for routine chemistry.",
		addedFields: MODERATE_ADDED,
		bestFor:
			"Routine agronomic decisions: lime/gypsum, NPK planning, sodicity screen.",
	},
	{
		level: SoilTestLevel.ADVANCED,
		label: "Advanced",
		summary:
			"Full lab panel: micronutrients, S, carbonates, SAR/ESP, optional heavy metals.",
		addedFields: ADVANCED_ADDED,
		bestFor:
			"Problem soils, high-value crops, certification-style reports, salinity/sodicity management.",
	},
] as const;

const LEVEL_RANK: Record<SoilTestLevel, number> = {
	[SoilTestLevel.PRELIMINARY]: 0,
	[SoilTestLevel.MODERATE]: 1,
	[SoilTestLevel.ADVANCED]: 2,
};

/** All fields expected at or below the declared level (inclusive hierarchy). */
export function expectedFieldsForLevel(
	level: SoilTestLevel
): SoilTestFieldSpec[] {
	const rank = LEVEL_RANK[level];
	const out: SoilTestFieldSpec[] = [];
	for (const std of SOIL_TEST_LEVEL_STANDARD) {
		if (LEVEL_RANK[std.level] <= rank) {
			out.push(...std.addedFields);
		}
	}
	return out;
}

/** Short comma-separated capture list for UI cards. */
export function captureSummaryForLevel(level: SoilTestLevel): string {
	const fields = expectedFieldsForLevel(level);
	return fields.map((f) => f.label).join(", ");
}

export function getSoilTestLevelStandard(
	level: SoilTestLevel
): SoilTestLevelStandard {
	const found = SOIL_TEST_LEVEL_STANDARD.find((s) => s.level === level);
	if (!found) {
		// Exhaustive enum — should never happen.
		return SOIL_TEST_LEVEL_STANDARD[0]!;
	}
	return found;
}

/** Markdown-friendly matrix for docs / standards UI. */
export const SOIL_TEST_LEVEL_MATRIX_MARKDOWN = `
| Level | Required / expected fields |
| ----- | -------------------------- |
| \`PRELIMINARY\` | sand, silt, clay, organicMatter, pH, EC, TDS |
| \`MODERATE\` | PRELIMINARY **+** Ca, Mg, K, Na, Cl, N, P, lab-CEC (if provided) |
| \`ADVANCED\` | MODERATE **+** Fe, Mn, Zn, Cu, B, Mo, S, heavy metals, carbonate, bicarbonate, SAR, ESP, full nutrient panel (extras via JSON where needed) |
`.trim();
